import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DisclaimerConsent {
  crisis_disclaimer_accepted: boolean;
  crisis_disclaimer_accepted_at: string | null;
  therapy_disclaimer_accepted: boolean;
  therapy_disclaimer_accepted_at: string | null;
}

export function useDisclaimerConsent(userId: string | undefined) {
  const [consent, setConsent] = useState<DisclaimerConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Load user's current consent status
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadConsent = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('crisis_disclaimer_accepted, crisis_disclaimer_accepted_at, therapy_disclaimer_accepted, therapy_disclaimer_accepted_at')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) throw error;
        setConsent(data as DisclaimerConsent);
      } catch (error) {
        console.error('Error loading disclaimer consent:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConsent();
  }, [userId]);

  // Accept crisis disclaimer
  const acceptCrisisDisclaimer = async () => {
    if (!userId) return false;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('profiles')
        .update({
          crisis_disclaimer_accepted: true,
          crisis_disclaimer_accepted_at: now
        })
        .eq('user_id', userId);

      if (error) throw error;

      setConsent(prev => prev ? {
        ...prev,
        crisis_disclaimer_accepted: true,
        crisis_disclaimer_accepted_at: now
      } : null);

      toast({
        title: "Consent Recorded",
        description: "Your acceptance of the crisis disclaimer has been saved.",
      });

      return true;
    } catch (error) {
      console.error('Error accepting crisis disclaimer:', error);
      toast({
        title: "Error",
        description: "Failed to record consent. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Accept therapy disclaimer
  const acceptTherapyDisclaimer = async () => {
    if (!userId) return false;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('profiles')
        .update({
          therapy_disclaimer_accepted: true,
          therapy_disclaimer_accepted_at: now
        })
        .eq('user_id', userId);

      if (error) throw error;

      setConsent(prev => prev ? {
        ...prev,
        therapy_disclaimer_accepted: true,
        therapy_disclaimer_accepted_at: now
      } : null);

      toast({
        title: "Consent Recorded",
        description: "Your acceptance of the therapy disclaimer has been saved.",
      });

      return true;
    } catch (error) {
      console.error('Error accepting therapy disclaimer:', error);
      toast({
        title: "Error", 
        description: "Failed to record consent. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    consent,
    loading,
    acceptCrisisDisclaimer,
    acceptTherapyDisclaimer,
    hasCrisisConsent: consent?.crisis_disclaimer_accepted || false,
    hasTherapyConsent: consent?.therapy_disclaimer_accepted || false
  };
}